/**
 * usePlayerState.js
 *
 * Phase 6 — Loads persisted vitals + inventory from the backend, exposes a
 * setter, and debounce-autosaves vitals/last-body to the backend every ~5s.
 *
 * Returns:
 *   { state, setState, inventory, refreshInventory, mutateInventory, deathCaches, refreshDeaths }
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const AUTOSAVE_INTERVAL_MS = 5000;

export function usePlayerState(api) {
  const [state, setState] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [deathCaches, setDeathCaches] = useState([]);
  const lastSavedRef = useRef(null);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, i, d] = await Promise.all([
          api.get('/api/player/state').then((r) => r.data).catch(() => null),
          api.get('/api/player/inventory').then((r) => r.data).catch(() => null),
          api.get('/api/player/deaths').then((r) => r.data).catch(() => []),
        ]);
        if (cancelled) return;
        setState(s);
        setInventory(i);
        setDeathCaches(Array.isArray(d) ? d : []);
      } catch (_e) {
        /* noop */
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Debounced autosave loop
  useEffect(() => {
    const flush = async () => {
      if (!pendingRef.current) return;
      const patch = pendingRef.current;
      pendingRef.current = null;
      try {
        const { data } = await api.put('/api/player/state', patch);
        lastSavedRef.current = Date.now();
        setState(data);
      } catch (_e) {
        // Re-queue on failure (lossy: only the latest patch survives)
        pendingRef.current = { ...patch, ...(pendingRef.current || {}) };
      }
    };
    timerRef.current = setInterval(flush, AUTOSAVE_INTERVAL_MS);
    return () => {
      clearInterval(timerRef.current);
      // Final flush on unmount
      flush();
    };
  }, [api]);

  const queueStatePatch = useCallback((patch) => {
    if (!patch) return;
    pendingRef.current = { ...(pendingRef.current || {}), ...patch };
    // Optimistically reflect locally
    setState((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const refreshInventory = useCallback(async () => {
    try {
      const { data } = await api.get('/api/player/inventory');
      setInventory(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, [api]);

  // Phase 9 — refresh persisted state from the server (e.g. after the city
  // refit endpoint updates HP/shield/oxygen or shop boosts max stats).
  const refreshState = useCallback(async () => {
    try {
      const { data } = await api.get('/api/player/state');
      setState(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, [api]);

  const mutateInventory = useCallback(async (patch) => {
    try {
      const { data } = await api.put('/api/player/inventory', patch);
      setInventory(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, [api]);

  const refreshDeaths = useCallback(async () => {
    try {
      const { data } = await api.get('/api/player/deaths');
      const list = Array.isArray(data) ? data : [];
      setDeathCaches(list);
      return list;
    } catch (_e) {
      return [];
    }
  }, [api]);

  return {
    state,
    queueStatePatch,
    inventory,
    refreshState,
    refreshInventory,
    mutateInventory,
    deathCaches,
    refreshDeaths,
  };
}
